import React from "react";

import { Container, Section, Eyebrow, Display, SEO } from "./ui";
import { canonicalFor } from "../routes";

function TermsOfService() {
  return (
    <>
      <SEO
        title="Terms of service — WhoBrew"
        description="The terms of service governing your use of WhoBrew."
        canonical={canonicalFor("/terms")}
      />
    <Section tone="page" spacing="loose">
      <Container width="prose">
        <Eyebrow>Legal</Eyebrow>
        <Display as="h1" scale="lg" italicWord="service" className="mt-5">
          Terms of service.
        </Display>
        <p className="mt-6 font-mono text-caption text-coffee-600 dark:text-cream-200 dark:text-cream-200 mb-12">Last updated: April 2026</p>

        <div className="space-y-6 text-coffee-700 dark:text-cream-200 leading-relaxed">

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">1. Introduction</h2>
          <p>
            Welcome to WhoBrew. These Terms of Service ("Terms") govern your use of the WhoBrew
            website, platform, and NFC-enabled business card services (collectively, the
            "Service"). By accessing or using the Service, you agree to be bound by these Terms.
            If you do not agree to these Terms, please do not use the Service.
          </p>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">2. Account terms</h2>
          <ul className="space-y-1.5 list-disc list-inside">
            <li>
              You must provide accurate, complete, and current information when creating an
              account. You are responsible for keeping your account information up to date.
            </li>
            <li>
              Each individual may maintain only one account. Creating multiple accounts may result
              in suspension or termination of all associated accounts.
            </li>
            <li>
              You are responsible for maintaining the security of your account credentials and
              for all activity that occurs under your account.
            </li>
            <li>
              You must be at least 18 years of age or the age of majority in your jurisdiction to
              create an account and use the Service.
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">3. Card services</h2>
          <p>
            WhoBrew provides NFC-enabled physical business cards paired with customizable digital
            profiles. Our Service includes:
          </p>
          <ul className="space-y-1.5 list-disc list-inside">
            <li>
              Physical NFC business cards that link to your digital profile when tapped or
              scanned.
            </li>
            <li>
              A digital profile that you can update at any time with your contact information,
              social links, portfolio, and other professional details.
            </li>
            <li>
              Analytics and insights about how your card is being used, including tap counts and
              engagement metrics.
            </li>
            <li>
              WhoBrew reserves the right to modify, suspend, or discontinue any aspect of the
              Service at any time with reasonable notice.
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">4. Acceptable use</h2>
          <p>You agree not to use the Service for any of the following purposes:</p>
          <ul className="space-y-1.5 list-disc list-inside">
            <li>
              Sending unsolicited messages, spam, or bulk communications through your digital
              profile or card links.
            </li>
            <li>
              Hosting, linking to, or distributing illegal, harmful, threatening, abusive, or
              otherwise objectionable content.
            </li>
            <li>
              Impersonating any person or entity, or falsely representing your affiliation with
              any person or organization.
            </li>
            <li>
              Attempting to gain unauthorized access to the Service, other user accounts, or any
              related systems or networks.
            </li>
            <li>
              Using the Service in any manner that could damage, disable, or impair its
              functionality or interfere with other users' access.
            </li>
          </ul>
          <p>
            WhoBrew reserves the right to suspend or terminate accounts that violate these
            guidelines without prior notice.
          </p>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">
            5. Intellectual property
          </h2>
          <p>
            The WhoBrew platform, including its design, logos, software, and all related
            intellectual property, is owned by WhoBrew and protected by applicable copyright,
            trademark, and other intellectual property laws. You may not reproduce, modify,
            distribute, or create derivative works based on the platform without our prior
            written consent.
          </p>
          <p>
            You retain full ownership of the content you upload to your digital profile,
            including text, images, and links. By using the Service, you grant WhoBrew a limited,
            non-exclusive license to display your content solely for the purpose of providing the
            Service to you and those who view your profile.
          </p>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">6. Payment terms</h2>
          <ul className="space-y-1.5 list-disc list-inside">
            <li>
              WhoBrew cards are available as a one-time purchase. The price covers the physical
              NFC card and access to your digital profile.
            </li>
            <li>
              All prices are listed in USD unless otherwise stated. Applicable taxes and shipping
              fees will be calculated at checkout.
            </li>
            <li>
              We offer a 30-day refund policy from the date of delivery. To request a refund,
              please contact us at{" "}
              <a
                href="mailto:support@whobrew.com"
                className="text-coffee-700 dark:text-cream-200 underline decoration-coffee-300 hover:text-coffee-900 transition-colors"
              >
                support@whobrew.com
              </a>
              . Refunds will be processed to the original payment method within 10 business days.
            </li>
            <li>
              WhoBrew reserves the right to change pricing at any time. Price changes will not
              affect orders that have already been placed.
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">
            7. Limitation of liability
          </h2>
          <p>
            To the fullest extent permitted by applicable law, WhoBrew and its officers,
            directors, employees, and agents shall not be liable for any indirect, incidental,
            special, consequential, or punitive damages, including but not limited to loss of
            profits, data, or business opportunities, arising out of or related to your use of
            the Service.
          </p>
          <p>
            WhoBrew's total liability for any claims arising under these Terms shall not exceed
            the amount you paid to WhoBrew for the Service in the twelve (12) months preceding
            the claim. The Service is provided on an "as is" and "as available" basis without
            warranties of any kind, whether express or implied.
          </p>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">
            8. Changes to terms
          </h2>
          <p>
            WhoBrew reserves the right to update or modify these Terms at any time. When we make
            changes, we will update the "Last updated" date at the top of this page. We may also
            notify you of significant changes via email or through the Service. Your continued
            use of the Service after any changes to the Terms constitutes your acceptance of the
            revised Terms. We encourage you to review this page periodically.
          </p>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">9. Contact</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us at{" "}
            <a
              href="mailto:support@whobrew.com"
              className="text-coffee-700 dark:text-cream-200 underline decoration-coffee-300 hover:text-coffee-900 transition-colors"
            >
              support@whobrew.com
            </a>
            . We will do our best to respond to your inquiry promptly.
          </p>

        </div>
      </Container>
    </Section>
    </>
  );
}

export default TermsOfService;
